import React, { useMemo, useState } from 'react';
import {
  Box, Checkbox, Chip, CircularProgress, Collapse, InputAdornment,
  Slider, Stack, TextField, Tooltip, Typography, alpha, useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ModelTrainingIcon from '@mui/icons-material/ModelTraining';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';

import { modelDisplayName, modelTechnicalName } from './modelNames';

// ---------------------------------------------------------------------------
// Shared option vocabulary for a goal's lifecycle settings.
//
// Both the goal-creation wizard (CreateGoalPage) and the per-goal settings
// tab (GoalSettingsCard) import from here so the two surfaces always offer
// the same options, labels and helper copy — there is exactly one source of
// truth for every control.
// ---------------------------------------------------------------------------

export const CADENCES = [
  { value: 60, label: 'Every minute' },
  { value: 300, label: 'Every 5 minutes' },
  { value: 900, label: 'Every 15 minutes' },
  { value: 3600, label: 'Hourly' },
  { value: 14400, label: 'Every 4 hours' },
  { value: 86400, label: 'Daily' },
  { value: 604800, label: 'Weekly' },
];

// Cadences offered when a policy mode is "scheduled" (retrain + discovery).
export const FREQUENCIES = [
  { value: 86400, label: 'Daily' },
  { value: 7 * 86400, label: 'Weekly' },
  { value: 30 * 86400, label: 'Monthly' },
];

// ---------------------------------------------------------------------------
// Automation policies — ONE vocabulary for the three per-goal actions
// (discovery re-runs, model retraining, forecasting). Each is
// auto | scheduled | manual with auto the default. Mirrors the backend's
// app/discovery/policies.py ('drift' is the accepted legacy alias of
// retrain auto — normalize it on read).
// ---------------------------------------------------------------------------

export const normalizePolicyMode = (mode) =>
  (mode === 'drift' ? 'auto' : ['auto', 'scheduled', 'manual'].includes(mode) ? mode : 'auto');

export const DISCOVERY_MODES = [
  {
    value: 'auto',
    label: 'Automatically — when data changes (recommended)',
    help: 'New signals are pre-checked the moment they arrive and drivers re-validate on fresh data or drift — no fixed clock, no wasted runs.',
  },
  {
    value: 'scheduled',
    label: 'On a schedule',
    help: 'A full discovery run happens on the cadence you pick, charged like a manual run.',
  },
  {
    value: 'manual',
    label: 'Manually — I re-run discovery myself',
    help: 'Discovery only runs when you hit Refit. Monitoring continues either way.',
  },
];

export const RETRAIN_MODES = [
  {
    value: 'auto',
    label: 'Automatically — when relationships drift (recommended)',
    help: 'When a validated driver starts weakening, the best model retrains automatically (at most once every 6 hours).',
  },
  {
    value: 'scheduled',
    label: 'On a schedule',
    help: 'The best model retrains on the cadence above, regardless of drift.',
  },
  {
    value: 'manual',
    label: 'Manually — just notify me',
    help: 'You get a notification when retraining looks worthwhile — nothing retrains without you.',
  },
];

export const SERVING_MODES = [
  {
    value: 'auto',
    label: 'Automatically — fresh forecast when new data lands (recommended)',
    help: 'A new forecast is produced at the goal cadence and the instant fresh data arrives. No new data means no wasted compute.',
  },
  {
    value: 'scheduled',
    label: 'On a schedule',
    help: 'Forecasts are produced on the cadence you pick instead of the goal cadence.',
  },
  {
    value: 'manual',
    label: 'Manually — I forecast on demand',
    help: 'Forecasts only happen when you hit Forecast now (or deploy a model).',
  },
];

export const AUTOPILOT_MODES = [
  {
    value: 'full',
    label: 'Full — promote and re-discover automatically',
    help: 'Retrains, re-discoveries and promotions all happen automatically. You get notifications, not chores.',
  },
  {
    value: 'review',
    label: 'Review — prepare everything, ask before promoting',
    help: 'The autopilot prepares everything but waits for you before deploying a new best model.',
  },
  {
    value: 'paused',
    label: 'Paused — freeze all automated actions',
    help: 'All automated actions are frozen. Monitoring continues and you still get drift notifications.',
  },
];

export const DEPLOY_MODES = [
  { value: 'auto', label: 'Deploy automatically (recommended)' },
  { value: 'review', label: 'Review, then I deploy' },
  { value: 'off', label: "Don't deploy" },
];

// Label shared by the create wizard + settings tab for the autopilot cap.
export const BUDGET_LABEL = 'Monthly automation budget (credits)';

// Span-picker bounds (steps), shared so create + settings agree.
export const HORIZON_MIN = 1;
export const HORIZON_MAX = 168;
export const LAG_MIN = 6;
export const LAG_MAX = 336;

export const TIER_ACCENT = { Light: 'success', Standard: 'info', Heavy: 'warning' };

// Relative compute weight of one of YOUR OWN models re-fit in the tournament.
// Must match model_library.USER_MODEL_COST_WEIGHT on the backend.
export const USER_MODEL_COST_WEIGHT = 2.5;

// Goal-type-aware copy for the horizon control. The underlying setting
// (horizon_bins) is identical across types — only the phrasing adapts.
export const GOAL_COPY = {
  forecast_value: {
    horizonTitle: 'Forecast how far ahead',
    horizonHelp: 'Each prediction projects this far into the future.',
    chipAhead: (span) => `Forecasts ${span} ahead`,
  },
  forecast_distribution: {
    horizonTitle: 'Forecast how far ahead',
    horizonHelp: 'Each prediction projects a likely range this far into the future.',
    chipAhead: (span) => `Forecasts ${span} ahead`,
  },
  trend: {
    horizonTitle: 'Predict direction this far ahead',
    horizonHelp: 'Each prediction calls the up/down move this far into the future.',
    chipAhead: (span) => `Predicts direction ${span} ahead`,
  },
  anomaly: {
    horizonTitle: 'Flag anomalies this far ahead',
    horizonHelp: 'Each prediction warns of an anomaly this far into the future.',
    chipAhead: (span) => `Flags anomalies ${span} ahead`,
  },
  rank_by_growth: {
    horizonTitle: 'Rank growth this far ahead',
    horizonHelp: 'Each member is scored on predicted change over this window, then ranked.',
    chipAhead: (span) => `Ranks growth ${span} ahead`,
  },
  rank_by_risk: {
    horizonTitle: 'Rank risk this far ahead',
    horizonHelp: 'Each member is scored on the chance it breaches its threshold inside this window, then ranked.',
    chipAhead: (span) => `Ranks risk ${span} ahead`,
  },
};
export const goalCopy = (goalType) => GOAL_COPY[goalType] || GOAL_COPY.forecast_value;

export const FLEET_GOAL_TYPES = ['rank_by_risk', 'rank_by_growth'];
export const isFleetGoalType = (t) => FLEET_GOAL_TYPES.includes(t);

// Fleet goals re-score a population; the three policy rows keep the same
// auto | scheduled | manual shape, with fleet verbs.
export const FLEET_RETRAIN_MODES = [
  {
    value: 'auto',
    label: 'Automatically — when rankings drift (recommended)',
    help: 'When member order starts shifting, the bake-off re-runs and the best ranker is kept (at most once every 6 hours).',
  },
  {
    value: 'scheduled',
    label: 'On a schedule',
    help: 'The bake-off re-runs on the cadence above, regardless of drift.',
  },
  {
    value: 'manual',
    label: 'Manually — just notify me',
    help: 'You get a notification when a new bake-off looks worthwhile — nothing re-races without you.',
  },
];

export const FLEET_SERVING_MODES = [
  {
    value: 'auto',
    label: 'Automatically — re-score when new data lands (recommended)',
    help: 'The whole fleet is re-scored at the goal cadence and the instant fresh data arrives. No new data means no wasted compute.',
  },
  {
    value: 'scheduled',
    label: 'On a schedule',
    help: 'The fleet is re-scored on the cadence you pick instead of the goal cadence.',
  },
  {
    value: 'manual',
    label: 'Manually — I score on demand',
    help: 'Members are only re-scored when you hit Score now.',
  },
];

export const FLEET_DEPLOY_MODES = [
  { value: 'auto', label: 'Start scoring automatically (recommended)' },
  { value: 'review', label: 'Review, then I start scoring' },
  { value: 'off', label: "Don't start scoring" },
];

// Turn a number of steps at a given cadence into a plain-language span,
// e.g. 48 steps @ 60s -> "48 minutes", 336 steps @ 60s -> "5 hours 36 minutes".
export const fmtSpan = (steps, intervalSeconds) => {
  if (!steps || !intervalSeconds) return null;
  let rem = steps * intervalSeconds;
  const units = [
    { s: 604800, one: 'week', many: 'weeks' },
    { s: 86400, one: 'day', many: 'days' },
    { s: 3600, one: 'hour', many: 'hours' },
    { s: 60, one: 'minute', many: 'minutes' },
    { s: 1, one: 'second', many: 'seconds' },
  ];
  const parts = [];
  for (const u of units) {
    if (rem >= u.s) {
      const n = Math.floor(rem / u.s);
      rem -= n * u.s;
      parts.push(`${n} ${n === 1 ? u.one : u.many}`);
    }
    if (parts.length === 2) break;
  }
  return parts.join(' ') || '0 seconds';
};

// Candidate spans (seconds) used to derive one-tap presets for the pickers.
const SPAN_PRESETS_S = [
  900, 1800, 3600, 7200, 10800, 21600, 43200,
  86400, 172800, 604800, 1209600, 2592000, 7776000,
];

// Short label for a span chip, e.g. 60min -> "1h", 1440min -> "1d".
export const fmtSpanShort = (steps, intervalSeconds) => {
  const total = steps * intervalSeconds;
  if (total % 604800 === 0) return `${total / 604800}w`;
  if (total % 86400 === 0) return `${total / 86400}d`;
  if (total % 3600 === 0) return `${total / 3600}h`;
  if (total % 60 === 0) return `${total / 60}m`;
  return `${total}s`;
};

// Interactive time picker: one-tap presets + a fine slider, all read in plain time.
export const SpanPicker = ({ icon, title, help, value, min, max, interval, overridden, onChange }) => {
  const theme = useTheme();
  const tint = theme.palette.primary.main;

  // Presets that land inside [min, max] steps for this cadence, evenly thinned to ~6.
  const seen = new Set();
  let presets = [];
  for (const s of SPAN_PRESETS_S) {
    const steps = Math.round(s / interval);
    if (steps >= min && steps <= max && !seen.has(steps)) {
      seen.add(steps);
      presets.push(steps);
    }
  }
  if (presets.length > 6) {
    const stride = (presets.length - 1) / 5;
    const thinned = [];
    for (let i = 0; i < 6; i += 1) thinned.push(presets[Math.round(i * stride)]);
    presets = Array.from(new Set(thinned));
  }
  const marks = presets.map((st) => ({ value: st }));

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.25 }}>
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Box sx={{ display: 'flex', color: 'text.secondary' }}>{icon}</Box>
          <Typography variant="body2" fontWeight={600}>{title}</Typography>
        </Stack>
        <Box
          sx={{
            px: 1,
            py: 0.25,
            borderRadius: 1.5,
            bgcolor: alpha(tint, overridden ? 0.18 : 0.1),
            border: `1px solid ${alpha(tint, overridden ? 0.45 : 0.22)}`,
            transition: 'background-color .15s, border-color .15s',
          }}
        >
          <Typography variant="caption" fontWeight={700} sx={{ color: tint, whiteSpace: 'nowrap' }}>
            {fmtSpan(value, interval)}
          </Typography>
        </Box>
      </Stack>
      {help && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
          {help}
        </Typography>
      )}

      {presets.length > 0 && (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 0.5 }}>
          {presets.map((st) => {
            const active = value === st;
            return (
              <Box
                key={st}
                role="button"
                onClick={() => onChange(st)}
                sx={{
                  cursor: 'pointer',
                  userSelect: 'none',
                  px: 1,
                  py: 0.25,
                  borderRadius: 1.5,
                  fontSize: 12,
                  fontWeight: 700,
                  lineHeight: 1.6,
                  color: active ? tint : 'text.secondary',
                  bgcolor: active ? alpha(tint, 0.16) : 'transparent',
                  border: `1px solid ${active ? alpha(tint, 0.5) : theme.palette.divider}`,
                  transition: 'all .12s',
                  '&:hover': { borderColor: alpha(tint, 0.6), color: tint },
                }}
              >
                {fmtSpanShort(st, interval)}
              </Box>
            );
          })}
        </Stack>
      )}

      <Box sx={{ px: 0.5 }}>
        <Slider
          value={value}
          min={min}
          max={max}
          marks={marks}
          onChange={(_e, v) => onChange(v)}
          valueLabelDisplay="auto"
          valueLabelFormat={(v) => fmtSpan(v, interval)}
          size="small"
          sx={{
            '& .MuiSlider-thumb': {
              width: 16,
              height: 16,
              transition: 'box-shadow .15s',
              '&:hover, &.Mui-focusVisible': { boxShadow: `0 0 0 8px ${alpha(tint, 0.16)}` },
            },
            '& .MuiSlider-mark': {
              width: 4,
              height: 4,
              borderRadius: '50%',
              bgcolor: alpha(tint, 0.5),
            },
            '& .MuiSlider-markActive': { bgcolor: tint },
          }}
        />
      </Box>
    </Box>
  );
};

const ListSubhead = ({ text }) => {
  const theme = useTheme();
  return (
    <Typography
      variant="caption"
      fontWeight={700}
      color="text.secondary"
      sx={{
        display: 'block', px: 1.5, py: 0.75, textTransform: 'uppercase',
        letterSpacing: 0.5, position: 'sticky', top: 0, zIndex: 1,
        bgcolor: 'background.paper', borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      {text}
    </Typography>
  );
};

const ModelRow = ({ checked, disabled, onClick, dotColor, title, badge, subtitle, right }) => {
  const theme = useTheme();
  return (
    <Box
      onClick={disabled ? undefined : onClick}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.75,
        cursor: disabled ? 'default' : 'pointer',
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
        '&:hover': disabled ? {} : { bgcolor: alpha(theme.palette.primary.main, 0.06) },
      }}
    >
      <Checkbox size="small" checked={checked} disabled={disabled} sx={{ p: 0.25 }} />
      {dotColor && <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: dotColor, flexShrink: 0 }} />}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={600} noWrap>
          {title}
          {badge && (
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
              · {badge}
            </Typography>
          )}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {right}
    </Box>
  );
};

/**
 * Controlled model picker shared by the create wizard and the settings tab.
 *
 * The lineup comes from one backend catalog (public models) plus the
 * workspace's own designs (blueprints). Selection is expressed as two sets the
 * parent owns:
 *   • ``deselected`` — ids of public models to EXCLUDE (always-on can't be).
 *   • ``bpSelected`` — uids of user blueprints to INCLUDE in the tournament.
 *
 * On any change the picker calls ``onChange({ deselected, bpSelected })`` with
 * the next sets so the parent can recompute cost (create) or persist
 * immediately (settings).
 */
export const ModelLineupPicker = ({
  models = [],
  modelsLoading = false,
  blueprints = [],
  blueprintsLoading = false,
  deselected,
  bpSelected,
  onChange,
  onEnterPick,
  ready = true,
  notReadyHint = 'Pick a signal and goal first.',
  title = 'Models',
  helper = 'We try the models that fit your data and keep the best.',
  showCost = true,
}) => {
  const theme = useTheme();
  const accent = (key) => theme.palette[key]?.main || theme.palette.primary.main;

  const [pickModels, setPickModels] = useState(
    () => (deselected?.size || 0) > 0 || (bpSelected?.size || 0) > 0);
  const [modelSearch, setModelSearch] = useState('');

  const relevantModels = useMemo(() => (models || []).filter((m) => m.relevant), [models]);
  const selectedModels = useMemo(
    () => relevantModels.filter((m) => m.always_on || !deselected.has(m.id)),
    [relevantModels, deselected],
  );

  const publicFiltered = useMemo(() => {
    const q = modelSearch.trim().toLowerCase();
    if (!q) return relevantModels;
    // Searchable by BOTH names: the friendly strategy name users see
    // ("Steady baseline") and the technical identity ("XGBoost ·
    // conservative") power users type.
    return relevantModels.filter((m) =>
      (m.display_name || '').toLowerCase().includes(q)
      || (m.label || '').toLowerCase().includes(q)
      || (m.engine || '').toLowerCase().includes(q)
      || (m.description || '').toLowerCase().includes(q));
  }, [relevantModels, modelSearch]);

  // The user's own models (blueprints they authored) — drop the
  // discovery-owned winners and anything without a trainable architecture.
  const bpAvailable = useMemo(() => {
    const trainable = (b) => {
      const mb = b?.model_blueprint || {};
      return Boolean((Array.isArray(mb.layers) && mb.layers.length)
        || mb.hyperparameters || mb.estimator);
    };
    return (blueprints || []).filter(
      (b) => b.uid && b.origin !== 'discovery' && trainable(b));
  }, [blueprints]);

  const bpFiltered = useMemo(() => {
    const q = modelSearch.trim().toLowerCase();
    if (!q) return bpAvailable;
    return bpAvailable.filter((b) =>
      (b.name || '').toLowerCase().includes(q)
      || (b.uid || '').toLowerCase().includes(q)
      || (b.framework || '').toLowerCase().includes(q));
  }, [bpAvailable, modelSearch]);

  const togglePublic = (id) => {
    const next = new Set(deselected);
    if (next.has(id)) next.delete(id); else next.add(id);
    onChange({ deselected: next, bpSelected });
  };

  const toggleBp = (uid) => {
    const next = new Set(bpSelected);
    if (next.has(uid)) next.delete(uid); else next.add(uid);
    onChange({ deselected, bpSelected: next });
  };

  // Master select-all/none — operates on the currently VISIBLE (filtered)
  // models so a search narrows what it toggles; selections outside the
  // filter are preserved. (``always_on`` models can't be unchecked.)
  const togglablePublic = useMemo(
    () => publicFiltered.filter((m) => !m.always_on),
    [publicFiltered],
  );
  const visibleSelectedCount = useMemo(() => {
    const pub = publicFiltered.filter((m) => m.always_on || !deselected.has(m.id)).length;
    const bp = bpFiltered.filter((b) => bpSelected.has(b.uid)).length;
    return pub + bp;
  }, [publicFiltered, bpFiltered, deselected, bpSelected]);
  const visibleTotal = publicFiltered.length + bpFiltered.length;
  const allVisibleSelected = visibleTotal > 0 && visibleSelectedCount === visibleTotal;
  const someVisibleSelected = visibleSelectedCount > 0 && visibleSelectedCount < visibleTotal;

  const toggleSelectAll = () => {
    const nextDeselected = new Set(deselected);
    const nextBp = new Set(bpSelected);
    if (allVisibleSelected) {
      togglablePublic.forEach((m) => nextDeselected.add(m.id));
      bpFiltered.forEach((b) => nextBp.delete(b.uid));
    } else {
      publicFiltered.forEach((m) => nextDeselected.delete(m.id));
      bpFiltered.forEach((b) => nextBp.add(b.uid));
    }
    onChange({ deselected: nextDeselected, bpSelected: nextBp });
  };

  const enterAllModels = () => {
    setPickModels(false);
    onChange({ deselected: new Set(), bpSelected: new Set() });
  };

  const enterPick = () => {
    setPickModels(true);
    if (onEnterPick) onEnterPick();
  };

  return (
    <>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <ModelTrainingIcon fontSize="small" sx={{ color: 'primary.main' }} />
          <Typography variant="subtitle2" fontWeight={700}>{title}</Typography>
        </Stack>
        {ready && (
          <Typography variant="caption" color="text.secondary">
            {modelsLoading ? 'Loading…'
              : pickModels ? `${selectedModels.length + bpSelected.size} selected`
              : `All ${relevantModels.length} models`}
          </Typography>
        )}
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25 }}>
        {helper}
      </Typography>

      {!ready ? (
        <Typography variant="caption" color="text.secondary">{notReadyHint}</Typography>
      ) : (
        <>
          <Stack direction="row" spacing={0.75} sx={{ mb: pickModels ? 1.5 : 0 }}>
            <Chip
              size="small"
              label="All models"
              onClick={enterAllModels}
              color={!pickModels ? 'primary' : 'default'}
              variant={!pickModels ? 'filled' : 'outlined'}
              sx={{ height: 28, fontWeight: 600 }}
            />
            <Chip
              size="small"
              label="Pick models"
              onClick={enterPick}
              color={pickModels ? 'primary' : 'default'}
              variant={pickModels ? 'filled' : 'outlined'}
              sx={{ height: 28, fontWeight: 600 }}
            />
          </Stack>

          <Collapse in={pickModels}>
            <TextField
              size="small"
              fullWidth
              value={modelSearch}
              onChange={(e) => setModelSearch(e.target.value)}
              placeholder="Search models…"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 1 }}
            />

            {/* Master select all / none */}
            <Box
              onClick={visibleTotal > 0 ? toggleSelectAll : undefined}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.5, mb: 1,
                cursor: visibleTotal > 0 ? 'pointer' : 'default',
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                '&:hover': visibleTotal > 0 ? { bgcolor: alpha(theme.palette.primary.main, 0.06) } : {},
              }}
            >
              <Checkbox
                size="small"
                checked={allVisibleSelected}
                indeterminate={someVisibleSelected}
                disabled={visibleTotal === 0}
                sx={{ p: 0.25 }}
              />
              <Typography variant="body2" fontWeight={700} sx={{ flex: 1, minWidth: 0 }}>
                {allVisibleSelected ? 'Deselect all' : 'Select all'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {visibleSelectedCount}/{visibleTotal} selected
              </Typography>
            </Box>

            <Box
              sx={{
                maxHeight: 300,
                overflowY: 'auto',
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
              }}
            >
              {/* Public models */}
              {publicFiltered.length > 0 && (
                <>
                  <ListSubhead text="Public models" />
                  {publicFiltered.map((m) => {
                    const on = m.always_on || !deselected.has(m.id);
                    const c = accent(TIER_ACCENT[m.cost_tier] || 'info');
                    return (
                      <ModelRow
                        key={m.id}
                        checked={on}
                        disabled={m.always_on}
                        onClick={() => togglePublic(m.id)}
                        dotColor={c}
                        title={modelDisplayName(m)}
                        badge={m.always_on ? 'always on' : null}
                        subtitle={m.description || modelTechnicalName(m) || m.engine}
                        right={showCost && m.cost_tier && (
                          <Tooltip title={m.metered ? 'Runs on GPU — adds usage at fit time' : 'Relative compute cost'} arrow>
                            <Chip
                              size="small"
                              icon={<BoltRoundedIcon sx={{ fontSize: 12 }} />}
                              label={`${m.cost_weight}\u00d7`}
                              variant="outlined"
                              sx={{ height: 20, fontWeight: 700, color: c, borderColor: alpha(c, 0.5), '& .MuiChip-icon': { color: c } }}
                            />
                          </Tooltip>
                        )}
                      />
                    );
                  })}
                </>
              )}

              {/* Your models — the user's own designs (blueprints). They
                  enter the tournament and are re-fit per fold, then
                  materialized as the serving model if they win. */}
              <ListSubhead text="Your models" />
              {bpFiltered.length === 0 && !blueprintsLoading && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 1.5, py: 1 }}>
                  {bpAvailable.length === 0 ? 'No models in this workspace yet.' : 'No matches.'}
                </Typography>
              )}
              {bpFiltered.map((b) => (
                <ModelRow
                  key={b.uid}
                  checked={bpSelected.has(b.uid)}
                  onClick={() => toggleBp(b.uid)}
                  dotColor={theme.palette.text.secondary}
                  title={b.name || b.uid}
                  subtitle={b.description || b.framework || 'Custom model'}
                  right={b.framework && (
                    <Chip size="small" variant="outlined" label={b.framework} sx={{ height: 20, fontSize: 10 }} />
                  )}
                />
              ))}
              {blueprintsLoading && bpFiltered.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 1 }}>
                  <CircularProgress size={18} />
                </Box>
              )}
            </Box>
          </Collapse>
        </>
      )}
    </>
  );
};
